/**
 * Session-wide vehicle image cache (in-memory).
 * Only stores FULLY READY image URLs (prefer blob:).
 * Entries are keyed by deviceId and tagged with a sourceKey (file location /
 * createdAt) so an updated VehicleImage invalidates the old blob.
 */

const readyByDeviceId = new Map(); // deviceId -> { url, sourceKey }
const inFlightByDeviceId = new Map();

export function getReadyVehicleImageUrl(deviceId) {
  if (!deviceId) return null;
  return readyByDeviceId.get(String(deviceId))?.url || null;
}

export function getReadyVehicleImageSourceKey(deviceId) {
  if (!deviceId) return null;
  return readyByDeviceId.get(String(deviceId))?.sourceKey || null;
}

/**
 * Drop a cached image when the vehicle's image document changes (or is removed).
 * Returns true if something was removed.
 */
export function invalidateVehicleImageIfStale(deviceId, sourceKey) {
  if (!deviceId) return false;
  const key = String(deviceId);
  const entry = readyByDeviceId.get(key);
  if (!entry) return false;

  // sourceKey null => image removed from vehicle
  if (entry.sourceKey === sourceKey) return false;

  if (typeof entry.url === "string" && entry.url.startsWith("blob:")) {
    URL.revokeObjectURL(entry.url);
  }
  readyByDeviceId.delete(key);
  return true;
}

export function setReadyVehicleImageUrl(deviceId, url, sourceKey = "") {
  if (!deviceId || !url) return;
  const key = String(deviceId);
  const previous = readyByDeviceId.get(key);

  // Never replace a blob with a plain http(s) URL for the same source.
  if (
    previous &&
    previous.sourceKey === sourceKey &&
    previous.url?.startsWith("blob:") &&
    !String(url).startsWith("blob:")
  ) {
    return;
  }

  if (previous?.url === url && previous?.sourceKey === sourceKey) return;

  if (
    previous?.url &&
    previous.url.startsWith("blob:") &&
    previous.url !== url
  ) {
    URL.revokeObjectURL(previous.url);
  }

  readyByDeviceId.set(key, { url, sourceKey: sourceKey || "" });
}

export function isVehicleImageFetchInFlight(deviceId) {
  return inFlightByDeviceId.has(String(deviceId));
}

export function markVehicleImageFetchInFlight(deviceId, promise) {
  const key = String(deviceId);
  inFlightByDeviceId.set(key, promise);
  const clear = () => {
    if (inFlightByDeviceId.get(key) === promise) {
      inFlightByDeviceId.delete(key);
    }
  };
  Promise.resolve(promise).then(clear, clear);
  return promise;
}

export function clearVehicleImageStore() {
  readyByDeviceId.forEach((entry) => {
    if (typeof entry?.url === "string" && entry.url.startsWith("blob:")) {
      URL.revokeObjectURL(entry.url);
    }
  });
  readyByDeviceId.clear();
  inFlightByDeviceId.clear();
}
